interface ManifoldMarket {
  id: string;
  slug: string;
  question: string;
  outcomeType: string;
  probability: number;
  isResolved: boolean;
  closeTime: string;
  volume: number;
  answers?: string[]; // Add support for multiple-choice markets
}

class ManifoldApiService {
  private apiUrl = 'https://api.manifold.markets/v0/markets';

  async fetchMarkets(): Promise<ManifoldMarket[]> {
    try {
      console.log('🔄 Fetching live data from Manifold Markets API via Edge Function...');

      // Construct the URL for your Supabase Edge Function
      const supabaseFunctionsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-api`;

      // Updated to fetch 500 markets instead of 50
      const apiUrlWithParams = `${this.apiUrl}?limit=500`;

      const response = await fetch(supabaseFunctionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          url: apiUrlWithParams,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn(`Manifold API error: ${response.status} - ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data: ManifoldMarket[] = await response.json();

      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from Manifold Markets API');
      }

      // Filter for only unresolved markets (both binary and multiple choice)
      const filteredMarkets = data.filter(market => 
        market.isResolved === false && 
        (market.outcomeType === 'BINARY' || market.outcomeType === 'MULTIPLE_CHOICE')
      );

      console.log('✅ Successfully fetched Manifold Markets data:', filteredMarkets.length, 'markets');
      return filteredMarkets;
    } catch (error) {
      console.warn('⚠️ Failed to fetch from Manifold Markets API via Edge Function:', error);
      console.log('📝 Returning fallback data');

      // Return fallback sample data
      return [
        {
          id: 'manifold-1',
          slug: 'bitcoin-150k-2025',
          question: 'Will Bitcoin reach $150,000 by end of 2025?',
          outcomeType: 'BINARY',
          probability: 0.35,
          isResolved: false,
          closeTime: '2025-12-31T23:59:59Z',
          volume: 2500000,
        },
        {
          id: 'manifold-2',
          slug: 'fed-rate-cut-q1-2025',
          question: 'Will the US Federal Reserve cut interest rates in Q1 2025?',
          outcomeType: 'BINARY',
          probability: 0.72,
          isResolved: false,
          closeTime: '2025-03-31T23:59:59Z',
          volume: 1800000,
        },
        {
          id: 'manifold-3',
          slug: 'tesla-300-june-2025',
          question: 'Will Tesla stock price exceed $300 by June 2025?',
          outcomeType: 'BINARY',
          probability: 0.41,
          isResolved: false,
          closeTime: '2025-06-30T23:59:59Z',
          volume: 950000,
        },
        {
          id: 'manifold-4',
          slug: 'us-recession-2025',
          question: 'Will there be a US recession in 2025?',
          outcomeType: 'BINARY',
          probability: 0.23,
          isResolved: false,
          closeTime: '2025-12-31T23:59:59Z',
          volume: 3200000,
        },
        {
          id: 'manifold-5',
          slug: 'ethereum-staking-5-percent-2025',
          question: 'Will Ethereum 2.0 staking rewards exceed 5% APY in 2025?',
          outcomeType: 'BINARY',
          probability: 0.68,
          isResolved: false,
          closeTime: '2025-12-31T23:59:59Z',
          volume: 1400000,
        },
        {
          id: 'manifold-6',
          slug: 'us-election-winner-2024',
          question: 'Who will win the 2024 US Presidential Election?',
          outcomeType: 'MULTIPLE_CHOICE',
          probability: 0.5,
          isResolved: false,
          closeTime: '2024-11-05T23:59:59Z',
          volume: 5000000,
          answers: ['Donald Trump', 'Kamala Harris', 'Other']
        },
        {
          id: 'manifold-7',
          slug: 'next-fed-chair-2025',
          question: 'Who will be the next Federal Reserve Chair in 2025?',
          outcomeType: 'MULTIPLE_CHOICE',
          probability: 0.5,
          isResolved: false,
          closeTime: '2025-02-01T23:59:59Z',
          volume: 1200000,
          answers: ['Jerome Powell', 'Lael Brainard', 'Neel Kashkari', 'Other']
        }
      ];
    }
  }

  async syncWithDatabase(markets: ManifoldMarket[]) {
    console.log('Syncing markets with database:', markets.length);
  }
}

export const manifoldApi = new ManifoldApiService();
export default ManifoldApiService;